import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type AdminRole = 'admin' | 'operator'

export interface AdminUser {
  id: string
  name: string
  email: string
  role: AdminRole
}

interface AuthState {
  user: AdminUser | null
  login: (email: string, password: string) => Promise<{ ok: true } | { ok: false; error: string; status: number }>
  logout: () => Promise<void>
}

/**
 * Resolve API base from Vite env (`VITE_API_BASE_URL`) cayendo a same-origin.
 * En dev el admin Vite corre en :5173 y proxyea a Next en :3000 (o se
 * configura VITE_API_BASE_URL=http://localhost:3000).
 */
function apiBase(): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const env = (import.meta as any).env || {}
  return env.VITE_API_BASE_URL || ''
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      // Default real: sin sesión hasta que el login custom devuelva un user.
      user: null,
      login: async (email, password) => {
        try {
          const res = await fetch(`${apiBase()}/api/admin/auth/login`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          })
          const data = (await res.json().catch(() => ({}))) as {
            ok?: boolean
            user?: AdminUser
            error?: string
          }
          if (!res.ok || !data.ok || !data.user) {
            return {
              ok: false,
              status: res.status,
              error: data.error || 'Credenciales inválidas',
            }
          }
          if (data.user.role !== 'admin' && data.user.role !== 'operator') {
            return {
              ok: false,
              status: 403,
              error: 'Tu cuenta no tiene acceso al panel',
            }
          }
          set({ user: data.user })
          return { ok: true }
        } catch (e) {
          console.error('admin login error', e)
          return { ok: false, status: 0, error: 'No se pudo conectar al servidor' }
        }
      },
      logout: async () => {
        try {
          await fetch(`${apiBase()}/api/admin/auth/login`, {
            method: 'DELETE',
            credentials: 'include',
          })
        } catch (e) {
          console.warn('admin logout: error contactando servidor', e)
        } finally {
          set({ user: null })
          // Redirigir al login para que el guard global vuelva a aplicar
          if (typeof window !== 'undefined') {
            window.location.href = '/login'
          }
        }
      },
    }),
    { name: 'maznet-admin-auth' },
  ),
)
